import { useState, useCallback, useEffect } from 'react';
import { Pagination, Response, SelectItemProps, Account } from '@ui/src';
import { useLazyGetAutoExchangeAccountsQuery } from '../store/services';

interface ListResponse {
  data: Response<Account>;
}

let timeout: NodeJS.Timeout;

const useGetAccountsAutocompleteList = () => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [pagination, setPagination] = useState<Pagination>();
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [getList, { isFetching, isLoading }] =
    useLazyGetAutoExchangeAccountsQuery();

  const getAccountBytId = async (accountId: string) => {
    const response = (await getList(
      `?all=true&ids=${accountId}`,
    )) as ListResponse;

    if (response?.data?.data?.edges?.length) {
      setAutocompleteList(
        response?.data?.data?.edges?.map((item) => ({
          name: item?.node?.description || '',
          suffix: item?.node?.fullNumber || '',
          value: item?.node?.id,
        })),
      );
    }
  };

  const getAutocompleteListAction = useCallback(async () => {
    const results: SelectItemProps[] = [];
    const accounts: Account[] = [];

    const response = (await getList('?take=20')) as ListResponse;

    if (response?.data?.data) {
      response.data.data.edges?.forEach((item) => {
        results.push({
          name: item?.node?.description || '',
          suffix: item?.node?.fullNumber || '',
          value: item?.node?.id,
        });

        accounts.push(item.node);
      });

      setPagination(response?.data?.data?.pageInfo);
    }

    setAutocompleteList(results);
  }, []);

  const onScroll = async () => {
    setNextPageIsLoading(true);

    const response = (await getList(
      `?take=20&cursor=${pagination?.endCursor}${searchValue ? `&search=${searchValue}` : ''}`,
    )) as ListResponse;

    if (response?.data?.data) {
      const nextAutocompleteList = ([] as SelectItemProps[]).concat(
        autocompleteList,
      );

      response?.data?.data?.edges?.forEach((item) => {
        if (
          !nextAutocompleteList?.find(
            (listItem) => listItem.value === item.node.id,
          )
        ) {
          nextAutocompleteList.push({
            name: item?.node?.description || '',
            suffix: item?.node?.fullNumber || '',
            value: item?.node?.id,
          });
        }
      });

      setAutocompleteList(nextAutocompleteList);
      setNextPageIsLoading(false);
      setPagination(response?.data?.data?.pageInfo);
    }
  };

  const onSearch = async (search: string) => {
    setSearchValue(search);
    clearTimeout(timeout);

    timeout = setTimeout(async () => {
      const response = (await getList(
        `?take=20${search ? `&search=${search}` : ''}`,
      )) as ListResponse;

      if (response?.data?.data) {
        const results: SelectItemProps[] = [];
        const accounts: Account[] = [];

        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item?.node?.description || '',
            suffix: item?.node?.fullNumber || '',
            value: item?.node?.id,
          });

          accounts.push(item.node);
        });

        setPagination(response?.data?.data?.pageInfo);
        setAutocompleteList(results);
      }
    }, 700);
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    nextPageIsLoading,
    pagination,
    onScroll,
    getAccountBytId,
  };
};

export default useGetAccountsAutocompleteList;
